import React, {useEffect, useState} from 'react';
import {handicaddieSelector} from "../../../services/store/asyncThunk";
import HandicaddieLogo from "../../../icons/HandicaddieLogo";
import {SessionState} from "../../../services/store/reducers/session/slice";
import TickIcon from "../../../icons/TickIcon";
import {IconSize} from "../../../services/IconConstants";
import {useNavigate} from "react-router-dom";
import {buildUrl, PAY_CADDIE, PLATFORM_HOME, REVIEW_CADDIE} from "../../../services/PageUtils";
import {PaymentService} from "../../../services/api/PaymentService";
import {Button, Container} from "react-bootstrap";
import Spinner from "../../../components/Spinner";
import CrossIcon from "../../../icons/CrossIcon";

interface Props {

}

const CompletedPaymentStatus = ({}: Props) => {

    const { user, sessionState } = handicaddieSelector((state) => state.session)
    const { paymentId } = handicaddieSelector((state) => state.postRound.urlParams)

    const nav = useNavigate();

    const { getAndRectifyPayment } = PaymentService();

    const [loading, setLoading] = useState<boolean>(true)
    const [inProgress, setInProgress] = useState<boolean>(false)

    const [wasSuccessful, setWasSuccessful] = useState<boolean>(false);

    const [remainingTime, setRemainingTime] = useState<number>(5);
    const [timerStarted, setTimerStarted] = useState<boolean>(false);

    useEffect(() => {
        if (sessionState !== SessionState.ACTIVE || !user || !paymentId) {
            return;
        }

        reloadPayment()
    }, [user, paymentId])

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;

        if (timerStarted) {
            interval = setInterval(() => {
                setRemainingTime(prev => prev - 1);
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timerStarted]); // start interval when timerStarted becomes true

    useEffect(() => {
        if (remainingTime <= 0) {
            goToReview(); // or whatever navigation you want
        }
    }, [remainingTime]);

    const reloadPayment = () => {
        if (paymentId) {
            if (!inProgress) {
                loadPayment(paymentId)
            }
            return;
        } else {
            setWasSuccessful(false)
            return;
        }
    }

    const goBack = () => {
        nav(buildUrl(PAY_CADDIE))
    }

    const loadPayment = async (id: string, attempt: number = 1) => {
        setLoading(true);
        setInProgress(true);
        try {
            let resp = await getAndRectifyPayment(id);
            if (resp.error) {
                throw new Error('Purchase load failed');
            }
            if (resp.payment && resp.payment.status) {
                if (resp.payment.status === 'FAILED') {
                    setWasSuccessful(false);
                    setLoading(false);
                    return;
                } else if (resp.payment.status === 'PAID') {
                    setWasSuccessful(true);
                    setLoading(false);
                    setTimerStarted(true);
                    return;
                }
            }
            throw new Error('Unexpected error');
        } catch (error) {
            if (attempt < 5) {
                setTimeout(() => {
                    loadPayment(id, attempt + 1);
                }, 2000); // wait 2 seconds
            } else {
                setWasSuccessful(false);
                setLoading(false)
                setInProgress(false);
            }
        }
    };

    const goToReview = () => {
        nav(buildUrl(REVIEW_CADDIE), { replace: true });
    }

    return (
        <Container>
            <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <div className={"findYourCaddie_logoContainer"}>
                    <HandicaddieLogo size={'lg'} />
                </div>

                {loading && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <Spinner onBackground />
                    <p>Completing your payment...</p>
                </div>}

                {!loading && wasSuccessful && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <TickIcon size={IconSize.bagXl} />
                    <h4>Payment successful.</h4>
                    <p>Your payment was successful. Thank you for trusting Handicaddie with your payment. If you've any feedback on your experience you can reach us at <code>help@handicaddie.com</code>.</p>
                    <br />
                    <br />
                    <p>Redirecting you to complete a review in {remainingTime} seconds...</p>
                </div>}

                {!loading && !wasSuccessful && <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <CrossIcon size={IconSize.bagXl} colour={'var(--color-error)'}/>
                    <p>Your payment was not successful.</p>
                    <p>If you think this is an error, don't panic, just reach out to us on <code>help@handicaddie.com</code> and we can look into it.</p>
                    <br />
                    <br />
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%', gap: '1em' }}>
                        <Button className={"reviewCaddie_bottomButton"} onClick={reloadPayment} variant={'secondary'}>
                            <span className={"reviewCaddie_btnText"}>RETRY</span>
                        </Button>
                        <Button className={"reviewCaddie_bottomButton_primary"} onClick={goBack}>
                            <span className={"reviewCaddie_btnText"}>BACK TO PAYMENT</span>
                        </Button>

                    </div>
                </div>}
            </div>
        </Container>
    )
}

export default CompletedPaymentStatus;